<script>
import store from '@state/store'
import { validationMixin } from 'vuelidate'
import { required, sameAs, not } from 'vuelidate/lib/validators'
import Datepicker from 'vuejs-datepicker'
import { parseISO } from 'date-fns/fp'
import { startOfToday } from 'date-fns'
import isDate from 'lodash/isDate'
import VueTagsInput from '@johmun/vue-tags-input'
import GoalTargetMixin from '@challenges/mixins/goal-target.vue'

const POST_TYPES_OPTIONS = [
  { value: 'donation', name: 'Donation' },
  { value: 'volunteered', name: 'Volunteering' },
  { value: 'kyndness', name: 'Kyndness' },
  { value: 'gratitude', name: 'Gratitude' },
  { value: 'kyndpoints', name: 'Kynd Points' },
]

const SETTING_OPTIONS = [
  { value: 'time', name: 'Time Based' },
  { value: 'number', name: 'Target Number' },
]

export default {
  components: {
    Datepicker,
    VueTagsInput,
  },
  mixins: [validationMixin, GoalTargetMixin],
  data() {
    return {
      formData: {
        name: '',
        postTypes: '',
        firstGroup: '',
        secondGroup: '',
        settings: 'time',

        goals: [
          {
            type: 'donation',
            goal: '',
            end: '',
          },
          {
            type: 'volunteered',
            goal: '',
            end: '',
          },
          {
            type: 'kyndness',
            goal: '',
            end: '',
          },
          {
            type: 'gratitude',
            goal: '',
            end: '',
          },
          {
            type: 'kyndpoints',
            goal: '',
            end: '',
          },
        ],
      },

      start: startOfToday(),
      end: '',
      groupsHierarchy: [],
      postTypesOptions: POST_TYPES_OPTIONS,
      settingOptions: SETTING_OPTIONS,
      loading: false,
      endFlag: false,
    }
  },
  validations: {
    formData: {
      name: {
        required,
      },
      firstGroup: {
        required,
      },
      secondGroup: {
        required,
        notSameAsfirstGroup: not(sameAs('firstGroup')),
      },
      postTypes: {
        required,
      },
    },
    end: {
      required,
    },
  },
  computed: {
    companyId() {
      const routeCompanyId = parseInt(this.$route.params.companyId, 10)
      if (routeCompanyId) {
        return routeCompanyId
      }
      const lastVisitedCompanyId = localStorage.getItem(
        'LAST_VISITED_COMPANY_ID'
      )
      if (lastVisitedCompanyId) {
        return +lastVisitedCompanyId
      }
      if (this.currentUser && this.currentUser.default_company_id) {
        return +this.currentUser.default_company_id
      }
      return ''
    },
  },
  watch: {
    end: {
      handler(val) {
        if (val) {
          this.endFlag = false
        }
      },
    },
  },
  mounted() {
    this.fetchGroupsHierarchy()
  },
  methods: {
    fetchGroupsHierarchy() {
      store
        .dispatch('adminGroups/fetchGroupsHierarchy', {
          companyId: this.companyId,
        })
        .then((data) => {
          if (data instanceof Array && data.length) {
            this.groupsHierarchy = data
          }
        })
    },
    filterData(data) {
      if (!data.length) return data
      const newData = data.map((item) => {
        return { id: item.id, name: item.text }
      })
      return newData
    },
    saveEvent() {
      if (this.loading) {
        return
      }
      if (!this.end) {
        this.endFlag = true
      }
      const vuelidate = this.$v.formData
      vuelidate.$touch()
      // if its still pending or an error is returned do not submit
      if (vuelidate.$pending || vuelidate.$error) {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' })
        return
      }
      this.loading = true
      // to form submit after this
      const {
        name,
        firstGroup,
        secondGroup,
        postTypes,
        goals,
        settings,
      } = this.formData

      const { start, end, orgTags, causeTags } = this

      function findId(id, arr) {
        return arr.reduce((a, item) => {
          if (a) return a
          if (item.id === id) return item
          if (item.children) return findId(id, item.children)
        }, null)
      }

      const groups = [
        findId(firstGroup, this.groupsHierarchy),
        findId(secondGroup, this.groupsHierarchy),
      ]

      const org = orgTags.length
        ? JSON.stringify(this.filterData(orgTags))
        : null
      const causesData =
        causeTags && causeTags.length
          ? JSON.stringify(this.filterData(causeTags))
          : null

      const params = {
        name,
        start: isDate(start) ? start.toISOString() : parseISO(start),
        end: isDate(end) ? end.toISOString() : parseISO(end),
        post_types: postTypes,
        groups,
        goals,
        goalSetting: settings,
        causes: causesData,
        charity_organization: org,
      }

      store
        .dispatch('challenges/newChallenge', { params })
        .then(() => {
          store.dispatch('auth/validate')
          alert('Competition was succesfully created!')
          this.$router.push({
            name: 'challengesList',
          })
        })
        .catch(() => console.warn('post new challenge went wrong'))
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<template>
  <div class="w-3/5 mx-auto">
    <div class="flex justify-between">
      <div class="mb-10 w-1/2 pr-6">
        <BaseInputText
          id="name"
          v-model="formData.name"
          :vuelidate="$v.formData.name"
          label="Challenge Name: *"
          placeholder="Name"
          required
        />
      </div>
    </div>

    <div class="flex justify-between">
      <div class="mb-10 w-1/2 pr-6">
        <BaseTreeSelect
          v-model="formData.firstGroup"
          :vuelidate="$v.formData.firstGroup"
          label="First Group: *"
          :options="groupsHierarchy"
        />
      </div>
      <div class="mb-10 w-1/2 pr-6">
        <BaseTreeSelect
          v-model="formData.secondGroup"
          :vuelidate="$v.formData.secondGroup"
          label="Second Group: *"
          :options="groupsHierarchy"
        />
      </div>
    </div>

    <div class="flex justify-between">
      <div class="mb-10 w-1/2 pr-6">
        <BaseFormGroup id="startDate" label="Start Date: *">
          <div class="relative">
            <Datepicker
              v-model="start"
              :disabled-dates="{ to: new Date() }"
              input-class="border cursor-pointer input border-gray-light focus:border-gray"
            />
            <span
              class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
            >
              <BaseIcon name="calendar" class="text-blue" :size="22" />
            </span>
          </div>
        </BaseFormGroup>
      </div>
      <div class="mb-10 w-1/2 pr-6">
        <BaseFormGroup id="endDate" label="End Date: *">
          <div class="relative">
            <Datepicker
              v-model="end"
              :vuelidate="$v.end"
              :disabled-dates="{ to: new Date() }"
              :input-class="[
                'border cursor-pointer input focus:border-gray',
                endFlag ? 'border-volunteer_red' : ' border-gray-light',
              ]"
            />
            <span
              class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
            >
              <BaseIcon name="calendar" class="text-blue" :size="22" />
            </span>
          </div>
        </BaseFormGroup>
        <div
          v-if="endFlag"
          class="mx-3 mt-2 text-sm errors text-volunteer_red text-right"
        >
          This field is required.
        </div>
      </div>
    </div>

    <h3 class="text-2xl text-blue-darker mb-5">Goals</h3>

    <div class="mb-10 w-1/2 pr-6">
      <BaseSelectBox
        id="postTypes"
        v-model="formData.postTypes"
        :vuelidate="$v.formData.postTypes"
        :options="postTypesOptions"
        label="What Kind of Challenge?: *"
      />
    </div>

    <div class="flex mb-10">
      <div class="w-1/2 pr-6">
        <BaseSelectBox
          id="goalSetting"
          v-model="formData.settings"
          :options="settingOptions"
          label="Challenge Settings:"
        />
      </div>
    </div>

    <div class="flex mb-10">
      <div class="w-1/2 pr-6">
        <label for="causes" class="block mb-3">
          Target a causes (optional):
        </label>
        <div class="relative" @click="getCauses">
          <VueTagsInput
            v-model="causeTag"
            :tags="causeTags"
            :autocomplete-items="causeDisplayTags"
            :add-only-from-autocomplete="true"
            :add-on-blur="false"
            :autocomplete-min-length="0"
            placeholder="What Cause?"
            @tags-changed="updateCauseTags"
          />
        </div>
      </div>
      <div class="w-1/2 pr-6">
        <label for="organization" class="block mb-3"
          >Target an organization (optional):</label
        >
        <VueTagsInput
          v-model="orgTag"
          :tags="orgTags"
          :max-tags="1"
          :maxlength="orgTags.length ? 0 : 10"
          :autocomplete-items="orgDisplayTags"
          :add-only-from-autocomplete="true"
          :add-on-blur="false"
          placeholder="What Organization?"
          @tags-changed="updateOrgTags"
        />
      </div>
    </div>

    <div v-if="formData.postTypes === 'donation'">
      <h4 class="text-lg text-blue-darker mb-1">Donation Challenge</h4>
      <div class="flex mb-6">
        <div class="w-1/2 pr-6">
          <BaseInputText
            id="amount"
            v-model="formData.goals[0].goal"
            label="Amount($):"
            required
          />
        </div>
        <div class="w-1/2 pr-6">
          <BaseFormGroup id="goalsEndDate" label="Until:">
            <div class="relative">
              <Datepicker
                v-model="formData.goals[0].end"
                :disabled-dates="{ to: new Date() }"
                input-class="border cursor-pointer input border-gray-light focus:border-gray"
              />
              <span
                class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
              >
                <BaseIcon name="calendar" class="text-blue" :size="22" />
              </span>
            </div>
          </BaseFormGroup>
        </div>
      </div>
    </div>

    <div v-if="formData.postTypes === 'volunteered'">
      <h4 class="text-lg text-blue-darker mb-1">Volunteer Challenge</h4>
      <div class="flex mb-6">
        <div class="w-1/2 pr-6">
          <BaseInputText
            id="volunteeredGoal"
            v-model="formData.goals[1].goal"
            label="Hours:"
            required
          />
        </div>
        <div class="w-1/2 pr-6">
          <BaseFormGroup id="goalsEndDate" label="Until:">
            <div class="relative">
              <Datepicker
                v-model="formData.goals[1].end"
                :disabled-dates="{ to: new Date() }"
                input-class="border cursor-pointer input border-gray-light focus:border-gray"
              />
              <span
                class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
              >
                <BaseIcon name="calendar" class="text-blue" :size="22" />
              </span>
            </div>
          </BaseFormGroup>
        </div>
      </div>
    </div>

    <div v-if="formData.postTypes === 'kyndness'">
      <h4 class="text-lg text-blue-darker mb-1"
        >Random Act of Kyndness Challenge</h4
      >
      <div class="flex mb-6">
        <div class="w-1/2 pr-6">
          <BaseInputText
            id="kyndnessGoal"
            v-model="formData.goals[2].goal"
            label="# of kyndness acts:"
            required
          />
        </div>
        <div class="w-1/2 pr-6">
          <BaseFormGroup id="goalsEndDate" label="Until:">
            <div class="relative">
              <Datepicker
                v-model="formData.goals[2].end"
                :disabled-dates="{ to: new Date() }"
                input-class="border cursor-pointer input border-gray-light focus:border-gray"
              />
              <span
                class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
              >
                <BaseIcon name="calendar" class="text-blue" :size="22" />
              </span>
            </div>
          </BaseFormGroup>
        </div>
      </div>
    </div>

    <div v-if="formData.postTypes === 'gratitude'">
      <h4 class="text-lg text-blue-darker mb-1">Gratitude Challenge</h4>
      <div class="flex mb-6">
        <div class="w-1/2 pr-6">
          <BaseInputText
            id="gratitudeGoal"
            v-model="formData.goals[3].goal"
            label="grateful for #:"
            required
          />
        </div>
        <div class="w-1/2 pr-6">
          <BaseFormGroup id="goalsEndDate" label="Until:">
            <div class="relative">
              <Datepicker
                v-model="formData.goals[3].end"
                :disabled-dates="{ to: new Date() }"
                input-class="border cursor-pointer input border-gray-light focus:border-gray"
              />
              <span
                class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
              >
                <BaseIcon name="calendar" class="text-blue" :size="22" />
              </span>
            </div>
          </BaseFormGroup>
        </div>
      </div>
    </div>

    <div v-if="formData.postTypes === 'kyndpoints'">
      <h4 class="text-lg text-blue-darker mb-1">Kynd Points Challenge</h4>
      <div class="flex mb-6">
        <div class="w-1/2 pr-6">
          <BaseInputText
            id="kyndpointsGoal"
            v-model="formData.goals[4].goal"
            label="Points:"
            required
          />
        </div>
        <div class="w-1/2 pr-6">
          <BaseFormGroup id="goalsEndDate" label="Until:">
            <div class="relative">
              <Datepicker
                v-model="formData.goals[4].end"
                :disabled-dates="{ to: new Date() }"
                input-class="border cursor-pointer input border-gray-light focus:border-gray"
              />
              <span
                class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
              >
                <BaseIcon name="calendar" class="text-blue" :size="22" />
              </span>
            </div>
          </BaseFormGroup>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-12">
      <button
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3.5"
        :disabled="loading"
        @click="saveEvent"
      >
        {{ loading ? 'Loading...' : 'Create' }}
      </button>
      <button
        class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3.5 ml-2"
      >
        <router-link :to="{ name: 'challengesList' }">
          Return
        </router-link>
      </button>
    </div>
  </div>
</template>
